import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import { GraphQlService } from '@shared/services/graphql-service';
import { catchError, map, of, switchMap } from 'rxjs';
import {
  AGENT_REGISTATION_SUMMARY,
  AGENT_REGISTRATION_PROGRESS,
  GET_SHIPPING_AGENT_TYPES,
  SAVE_SHIPPING_AGENT,
  SUBMIT_AGENT_APPLICATION,
} from '../graphql/shipping-agent-regrastration.graphql';
import * as ActionsGroup from './shipping-agent-registration.actions';

@Injectable({
  providedIn: 'root',
})
export class ShippingAgentRegistrationEffects {
  private actions$ = inject(Actions);
  private graphQLService = inject(GraphQlService);

  $saveShippingAgent = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionsGroup.SaveShippingAgentActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .mutate<any>({
            mutation: SAVE_SHIPPING_AGENT,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.saveAgent;
              if (payload?.status === false || payload?.fieldsErrors?.length) {
                return ActionsGroup.SaveShippingAgentActions.failure({
                  error: payload,
                });
              }
              return ActionsGroup.SaveShippingAgentActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(ActionsGroup.SaveShippingAgentActions.failure({ error })),
            ),
          ),
      ),
    ),
  );

  $agentRegistrationProgress = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionsGroup.AgentRegistrationProgressActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<any>({
            query: AGENT_REGISTRATION_PROGRESS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { agentUuid: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.agentRegistrationProgress;
              if (payload?.status === false) {
                return ActionsGroup.AgentRegistrationProgressActions.failure({
                  error: payload,
                });
              }
              return ActionsGroup.AgentRegistrationProgressActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(ActionsGroup.AgentRegistrationProgressActions.failure({ error })),
            ),
          ),
      ),
    ),
  );

  $agentRegistrationSummary = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionsGroup.AgentRegistrationSummaryActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<any>({
            query: AGENT_REGISTATION_SUMMARY,
            apolloNamespace: ApolloNamespace.registration,
            variables: { agentUuid: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.agentRegistrationSummary;
              if (payload?.status === false) {
                return ActionsGroup.AgentRegistrationSummaryActions.failure({
                  error: payload,
                });
              }
              return ActionsGroup.AgentRegistrationSummaryActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(ActionsGroup.AgentRegistrationSummaryActions.failure({ error })),
            ),
          ),
      ),
    ),
  );

  $submitAgentApplication = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionsGroup.SubmitAgentApplicationActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .mutate<any>({
            mutation: SUBMIT_AGENT_APPLICATION,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.submitAgentApplication;
              if (payload?.status === false || payload?.fieldsErrors?.length) {
                return ActionsGroup.SubmitAgentApplicationActions.failure({
                  error: payload,
                });
              }
              return ActionsGroup.SubmitAgentApplicationActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(ActionsGroup.SubmitAgentApplicationActions.failure({ error })),
            ),
          ),
      ),
    ),
  );

  $getShippingAgentTypes = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionsGroup.GetShippingAgentTypesActions.request),
      switchMap(() =>
        this.graphQLService
          .query<any>({
            query: GET_SHIPPING_AGENT_TYPES,
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.getAgentTypes;
              if (payload?.status === false) {
                return ActionsGroup.GetShippingAgentTypesActions.failure({
                  error: payload,
                });
              }
              return ActionsGroup.GetShippingAgentTypesActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(ActionsGroup.GetShippingAgentTypesActions.failure({ error })),
            ),
          ),
      ),
    ),
  );
}
